import { LocalCliError } from "./errors.js";
import type { RepoState } from "./github-app-repo.js";

export type CollisionAction = "create" | "reuse" | "adopt";
export type CollisionChoice = "reuse" | "new" | "abort";
export interface CollisionDecision {
  repoName: string;
  action: CollisionAction;
}

const DEFAULT_MAX_ATTEMPTS = 20;

/** The direct action for a non-foreign state. */
function actionForState(state: RepoState): CollisionAction {
  switch (state) {
    case "absent":
      return "create";
    case "m8t-brain":
      return "reuse";
    case "empty":
      return "adopt";
    case "foreign":
      throw new LocalCliError({
        code: "FOREIGN_REPO_COLLISION",
        message: `A repo already exists but isn't an m8t brain.`,
      });
  }
}

/** Candidate name for the Nth attempt: base, then base-2, base-3, … */
function candidate(base: string, n: number): string {
  return n === 1 ? base : `${base}-${n.toString()}`;
}

/** Scan base, base-2, … for the first candidate whose state `accept`s. */
async function scan(
  base: string,
  classify: (name: string) => Promise<RepoState>,
  accept: (state: RepoState) => boolean,
  startAt: number,
  maxAttempts: number,
): Promise<{ repoName: string; state: RepoState }> {
  for (let n = startAt; n <= maxAttempts; n++) {
    const name = candidate(base, n);
    const state = await classify(name);
    if (accept(state)) return { repoName: name, state };
  }
  throw new LocalCliError({
    code: "REPO_NAME_SCAN_EXHAUSTED",
    message: `Could not find a free repo name for "${base}" after ${maxAttempts.toString()} attempts.`,
  });
}

/**
 * Decide what to do about a target repo name given its state + context.
 * Foreign collisions never clobber: headless → throw; interactive → prompt
 * (rename via suffix scan, or abort).
 */
export async function resolveRepoCollision(args: {
  repoName: string;
  classify: (name: string) => Promise<RepoState>;
  interactive: boolean;
  force?: "reuse" | "new";
  prompt?: (info: { repoName: string; state: RepoState }) => Promise<CollisionChoice>;
  maxAttempts?: number;
}): Promise<CollisionDecision> {
  const max = args.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const state = await args.classify(args.repoName);

  // --new: always allocate a fresh (absent) name.
  if (args.force === "new") {
    const found = await scan(args.repoName, args.classify, (s) => s === "absent", 1, max);
    return { repoName: found.repoName, action: "create" };
  }

  // --reuse: apply the direct action; foreign throws (can't reuse an unrelated repo).
  if (args.force === "reuse") {
    return { repoName: args.repoName, action: actionForState(state) };
  }

  if (state === "absent") return { repoName: args.repoName, action: "create" };

  // Interactive: prompt on any collision.
  if (args.interactive && args.prompt) {
    const choice = await args.prompt({ repoName: args.repoName, state });
    if (choice === "abort") {
      throw new LocalCliError({ code: "BRAIN_CREATE_ABORTED", message: "Aborted by the operator." });
    }
    if (choice === "new") {
      // First non-foreign candidate, starting after the base name.
      const found = await scan(args.repoName, args.classify, (s) => s !== "foreign", 2, max);
      return { repoName: found.repoName, action: actionForState(found.state) };
    }
    // "reuse" chosen — only offered for non-foreign states.
    return { repoName: args.repoName, action: actionForState(state) };
  }

  // Non-interactive default: reuse/adopt a valid brain; foreign throws.
  if (state === "foreign") {
    throw new LocalCliError({
      code: "FOREIGN_REPO_COLLISION",
      message:
        `A repo with this name already exists but isn't an m8t brain, so it won't be touched. ` +
        `Rename that repo or install into a different org, then re-run.`,
    });
  }
  return { repoName: args.repoName, action: actionForState(state) };
}
